import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { IApplicationState } from "../../redux/reducers";
import ReactImageFallback from "react-image-fallback";
import {
	FaBrain,
	FaCertificate,
	FaFlag,
	FaGlobe,
	FaUser,
	FaInfoCircle,
	FaMapPin,
	FaEdit,
	FaBox,
	FaPhone
} from "react-icons/fa";
import AIAgentBox from "../../components/common/AIAgentBox";
import AIAnalysisResultsModal from "../../components/modals/AIAnalysisResultsModal";
import { MoreVertical } from "react-feather";
import { getIngredientImagePlaceholder } from "../../utils/ingredient";
import supplierService from "../../services/supplierService";
import { Button, Table, Progress } from "reactstrap";

interface IProps extends RouteComponentProps {
	allSupplier: any;
	getAllSupplierByUser: () => Promise<any>;
}

const AdminSupplierTable: React.FC<IProps> = ({ history, getAllSupplierByUser, allSupplier }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [menuOpen, setMenuOpen] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [isAIAnalysisModalOpen, setIsAIAnalysisModalOpen] = useState(false);

	const renderStatusBadge = (status: string) => {
		const statusClass = `badge px-3 py-1 rounded-pill supplier-status-${status.toLowerCase()}`;
		let bg = "bg-secondary";
		if (status === "Active") bg = "bg-success";
		else if (status === "Inactive") bg = "bg-danger";
		return <span className={`${statusClass} ${bg}`}>{status}</span>;
	};

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				await getAllSupplierByUser();
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	}, [getAllSupplierByUser]);

	// Toggle overflow menu
	const toggleMenu = (id: string, e: React.MouseEvent) => {
		e.stopPropagation();
		setMenuOpen(menuOpen === id ? null : id);
	};

	// Close on outside click
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (!target.closest(".supplier-actions-menu") && !target.closest(".actions-toggle-btn")) {
				setMenuOpen(null);
			}
		};
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	// Filter suppliers based on search
	const filteredSuppliers = allSupplier?.filter((supplier: any) => {
		if (!search) return true;
		const searchLower = search.toLowerCase();
		return (
			supplier.name?.toLowerCase().includes(searchLower) ||
			supplier.type?.toLowerCase().includes(searchLower) ||
			supplier.location?.toLowerCase().includes(searchLower) ||
			supplier.status?.toLowerCase().includes(searchLower)
		);
	}) || [];

	return (
		<div className="container-fluid">
			{/* AI Suppliers Agent Card */}
			<AIAgentBox
				title="AI Suppliers Agent"
				description="Analyze supplier profiles to find missing data, enrich with external sources, and identify optimization opportunities"
				features={[
					{ icon: <FaCertificate />, text: "Find missing certifications", iconColor: "#28a745" },
					{ icon: <FaUser />, text: "Enrich contact information", iconColor: "#0d6efd" },
					{ icon: <FaGlobe />, text: "Update CO₂ impact data", iconColor: "#17a2b8" },
					{ icon: <FaFlag />, text: "Flag duplicates & inactive", iconColor: "#dc3545" }
				]}
				theme="green"
				onButtonClick={() => setIsAIAnalysisModalOpen(true)}
			/>

			{/* Search */}
			<div className="admin-data-search">
				<div className="admin-data-search__input-wrapper">
					<input
						type="text"
						placeholder="Search suppliers by name, type, location, or status..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="admin-data-search__input"
						aria-label="Search suppliers"
					/>
					<i className="fas fa-search admin-data-search__icon"></i>
					{search && (
						<button
							type="button"
							className="admin-data-search__clear-btn"
							onClick={() => setSearch("")}
							aria-label="Clear search"
						>
							<i className="fas fa-times"></i>
						</button>
					)}
				</div>
				<div className="admin-data-search__actions">
					{/* <button
						type="button"
						className="admin-data-search__button btn-primary"
						onClick={() => history.push("/app/supplier/add")}
					>
						<i className="fas fa-plus"></i>
						Add Supplier
					</button> */}
				</div>
			</div>

			{/* Supplier Table */}
			<div className="admin-data-table-wrapper">
				<div className="table-responsive">
					<Table className="admin-data-table suppliers-table">
						<thead>
							<tr className="table-header">
								<th>Supplier</th>
								<th>Type</th>
								<th>Location</th>
								<th>Status</th>
								<th>Completeness</th>
								<th>Usage</th>
								<th className="actions-column">Actions</th>
							</tr>
						</thead>
						<tbody>
							{isLoading ? (
								<tr>
									<td colSpan={7} className="loading-cell">
										<div className="loading-spinner">
											<div className="spinner"></div>
											<span>Loading suppliers...</span>
										</div>
									</td>
								</tr>
							) : !filteredSuppliers || filteredSuppliers.length === 0 ? (
								<tr>
									<td colSpan={7} className="empty-cell">
										<div className="empty-state">
											<div className="empty-icon">🏢</div>
											<span>{search ? "No suppliers match your search" : "No suppliers found"}</span>
										</div>
									</td>
								</tr>
							) : (
								filteredSuppliers.map((supplier: any) => (
									<tr
										key={supplier._id}
										className="table-row clickable"
										onClick={(e) => {
											const target = e.target as HTMLElement;
											if (
												target.closest(".supplier-actions-menu") ||
												target.closest(".actions-toggle-btn")
											) {
												return;
											}
											history.push(`/app/supplier/${supplier._id}`);
										}}
									>
										{/* Supplier Info */}
										<td className="cell-name">
											<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
												<ReactImageFallback
													src={supplier.image_url || "/placeholder.svg"}
													fallbackImage={getIngredientImagePlaceholder()}
													alt={supplier.name}
													className="rounded-circle border"
													style={{ width: 32, height: 32 }}
												/>
												<div>
													<div style={{ fontWeight: 500 }}>{supplier.name}</div>
													<div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
														{(supplier.certifications || []).slice(0, 2).map((cert: string, idx: number) => (
															<span key={idx} className="badge badge-secondary" style={{ fontSize: '10px' }}>
																{cert}
															</span>
														))}
														{(supplier.certifications || []).length > 2 && (
															<span className="badge badge-light" style={{ fontSize: '10px' }}>
																+{(supplier.certifications || []).length - 2}
															</span>
														)}
													</div>
												</div>
											</div>
										</td>
										<td>{supplier.type || "N/A"}</td>
										<td>
											<FaMapPin style={{ marginRight: '4px', color: '#dc3545' }} size={12} />
											{supplier.location || "Unknown"}
										</td>
										<td className="cell-status">
											<span className={`status-badge status-${(supplier.status || "inactive").toLowerCase()}`}>
												{supplier.status || "Inactive"}
											</span>
										</td>
										<td>
											<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
												<div style={{
													width: '60px',
													height: '6px',
													backgroundColor: '#e9ecef',
													borderRadius: '3px',
													overflow: 'hidden'
												}}>
													<div style={{
														width: `${supplier.completeness || 0}%`,
														height: '100%',
														backgroundColor: supplier.completeness > 70 ? '#28a745' : supplier.completeness > 40 ? '#ffc107' : '#dc3545',
														transition: 'width 0.3s ease'
													}} />
												</div>
												<span style={{ fontSize: '12px', color: '#6c757d' }}>{supplier.completeness || 0}%</span>
											</div>
										</td>
										<td>
											<span style={{ fontWeight: 500 }}>{supplier.usage || 0}</span>
											<span style={{ color: '#6c757d', fontSize: '12px', marginLeft: '4px' }}>products</span>
										</td>
										<td className="actions-cell">
											<div className="btn-group">
												<button
													type="button"
													className="action-btn btn-edit"
													onClick={(e) => {
														e.stopPropagation();
														history.push(`/app/supplier/edit/${supplier._id}`);
													}}
												>
													Edit
												</button>
												<button
													type="button"
													className="action-btn btn-view"
													onClick={(e) => {
														e.stopPropagation();
														history.push(`/app/supplier/${supplier._id}/products`);
													}}
												>
													View
												</button>
											</div>
										</td>

									</tr>
								))
							)}
						</tbody>
					</Table>
				</div>
			</div>

			{/* AI Analysis Results Modal */}
			<AIAnalysisResultsModal
				isOpen={isAIAnalysisModalOpen}
				toggle={() => setIsAIAnalysisModalOpen(false)}
				collectionName="company"
				collectionLabel="Suppliers"
				onAnalysisComplete={() => {
					// Refresh suppliers list after analysis is applied
					getAllSupplierByUser();
				}}
			/>
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	allSupplier: state.supplier.result
});

const mapDispatchToProps = {
	getAllSupplierByUser: () => supplierService.getAllSupplierByUser()
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AdminSupplierTable));
